import React, { useEffect, useMemo, useState } from 'react';

import { getLoadingTip } from '../utils/loadingTips';
import { ParametricSphereArt } from './ParametricSphereArt';

const TRANSITION_OVERLAY_Z_INDEX = 5000;
const TRANSITION_OVERLAY_BG_RGBA = 'rgba(0, 0, 0, 0.92)';

const TRANSITION_TITLE_COLOR = '#c9d1d9';
const TRANSITION_BAR_BG = '#21262d';
const TRANSITION_BAR_FILL = '#58a6ff';

interface TransitionOverlayProps {
  isVisible: boolean;
  message: string;
}

export const TransitionOverlay: React.FC<TransitionOverlayProps> = ({ isVisible, message }) => {
  const safeMessage: string = message.trim() ? message.trim() : 'Loading...';

  const [tipIndex, setTipIndex] = useState<number>(0);

  useEffect(() => {
    if (!isVisible) return;

    setTipIndex(0);

    const intervalId: number = window.setInterval(() => {
      setTipIndex((currentIndex: number) => currentIndex + 1);
    }, 4500);

    return () => window.clearInterval(intervalId);
  }, [isVisible, safeMessage]);

  const currentTip: string = useMemo(() => getLoadingTip(tipIndex), [tipIndex]);

  if (!isVisible) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: TRANSITION_OVERLAY_Z_INDEX,
        backgroundColor: TRANSITION_OVERLAY_BG_RGBA,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
      }}
    >
      {/* Background parametric sphere visualization */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1,
          pointerEvents: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <ParametricSphereArt
          width={500}
          height={500}
          dotSpacing={3}
          opacity={0.3}
        />
      </div>
        <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 2,
          pointerEvents: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        }}
      >
      </div>
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          width: '100%',
          fontFamily: 'var(--font-mono)',
          maxWidth: 760,
          padding: 24,
          boxSizing: 'border-box',
          textAlign: 'center',
        }}
      >
        <div style={{ color: TRANSITION_TITLE_COLOR, fontSize: 14, fontWeight: 700, letterSpacing: 1 }}>
           {safeMessage}
        </div>

        <div style={{ marginTop: 10, color: '#94a3b8', fontSize: 12, letterSpacing: 0.2 }}>
          {currentTip}
        </div>

        <div style={{ width: '100%', maxWidth: 620, margin: '16px auto 0 auto', overflow: 'hidden' }}>
          <div style={{ height: 10, background: TRANSITION_BAR_BG, border: '1px solid #30363d', overflow: 'hidden' }}>
            <div
              style={{
                height: '100%',
                width: '35%',
                background: TRANSITION_BAR_FILL,
                animation: 'grandLineLoadingSweep 1.1s ease-in-out infinite',
              }}
            />
          </div>
        </div>

        <style>
          {`
            @keyframes grandLineLoadingSweep {
              0% { transform: translateX(-100%); opacity: 0.35; }
              50% { transform: translateX(200%); opacity: 1; }
              100% { transform: translateX(400%); opacity: 0.35; }
            }
          `}
        </style>
      </div>
    </div>
  );
};
